const LOCALE_BY_CURRENCY: Record<string, string> = {
  USD: 'en-US',
  INR: 'en-IN',
  GBP: 'en-GB',
  EUR: 'de-DE',
};

export function currencyLocale(code?: string | null): string {
  if (!code) return 'en-US';
  return LOCALE_BY_CURRENCY[String(code).trim().toUpperCase()] || 'en-US';
}

const SYMBOL_BY_CODE: Record<string, string> = {
  USD: '$',
  INR: '₹',
  GBP: '£',
  EUR: '€',
};

export function currencySymbolForCode(code?: string | null, explicitSymbol?: string | null): string {
  const sym = String(explicitSymbol || '').trim();
  if (sym) return sym;
  const normalized = String(code || '').trim().toUpperCase();
  return SYMBOL_BY_CODE[normalized] || normalized;
}

export function formatCurrencyAmount(
  symbol: string,
  value: number,
  currencyCode?: string | null,
  options?: Intl.NumberFormatOptions
): string {
  const locale = currencyLocale(currencyCode);
  const displaySymbol = currencySymbolForCode(currencyCode, symbol);
  const formatted = Math.max(0, Number(value) || 0).toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  });
  return `${displaySymbol}${formatted}`;
}

function compactUnitLabel(value: number, divisor: number, suffix: string): string {
  const n = Math.max(0, Math.floor(Number(value) || 0));
  if (n % divisor === 0) return `${n / divisor}${suffix}`;
  // Floor to one decimal so ~99.99L does not round up to "100lk" (misread as 1cr).
  const scaled = Math.floor((n / divisor) * 10) / 10;
  return `${scaled}${suffix}`;
}

/** Seat HUD shorthand — 10k, 1lk, 1cr, 10cr (no currency symbol) */
export function formatCompactCurrencyAmount(value: number): string {
  const n = Math.max(0, Math.floor(Number(value) || 0));
  if (n >= 10_000_000) return compactUnitLabel(n, 10_000_000, 'cr');
  if (n >= 100_000) return compactUnitLabel(n, 100_000, 'lk');
  if (n >= 1_000) return compactUnitLabel(n, 1_000, 'k');
  return String(n);
}

function formatScaledUnit(value: number, divisor: number, suffix: string): string {
  const n = Math.max(0, Math.floor(Number(value) || 0));
  if (n % divisor === 0) return `${n / divisor}${suffix}`;
  const scaled = Math.round((n / divisor) * 10) / 10;
  return `${scaled}${suffix}`;
}

/** Action-button stake labels — 10L, 20L, 1.6Cr */
export function formatOctroStake(value: number): string {
  const n = Math.max(0, Math.floor(Number(value) || 0));
  if (n >= 10_000_000) return formatScaledUnit(n, 10_000_000, 'Cr');
  if (n >= 100_000) return formatScaledUnit(n, 100_000, 'L');
  if (n >= 1_000) return formatScaledUnit(n, 1_000, 'K');
  return String(n);
}

/** Table limit numbers — 10,00,000 (Indian grouping when INR) */
export function formatGroupedAmount(value: number, currencyCode?: string | null): string {
  const n = Math.max(0, Math.floor(Number(value) || 0));
  return n.toLocaleString(currencyLocale(currencyCode), { maximumFractionDigits: 0 });
}

const SEAT_NAME_MAX_LEN = 10;

/** Seat pill username — first 10 chars then ".." */
export function formatSeatPlayerName(username: string, isMe = false): string {
  if (isMe) return 'you';
  const name = String(username || 'Player').trim() || 'Player';
  if (name.length <= SEAT_NAME_MAX_LEN) return name;
  return `${name.slice(0, SEAT_NAME_MAX_LEN)}..`;
}
